export type BrowserTimezonePayload = {
  customer_timezone: string;
  customer_timezone_offset_minutes: number;
};

const TZ_SUFFIX_RE = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

export function getBrowserTimezonePayload(): BrowserTimezonePayload {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  return {
    customer_timezone: timezone,
    customer_timezone_offset_minutes: -new Date().getTimezoneOffset(),
  };
}

function parseDateParts(raw: string): Date | null {
  const s = raw.trim();
  if (!s) return null;

  if (TZ_SUFFIX_RE.test(s)) {
    const d = new Date(s.replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  return new Date(Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4] || 0),
    Number(m[5] || 0),
    Number(m[6] || 0)
  ));
}

function coerceToDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value > 10_000_000_000 ? value : value * 1000;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    const s = value.trim();
    if (/^\d+$/.test(s)) return coerceToDate(Number(s));
    return parseDateParts(s);
  }
  return null;
}

export function browserTimezoneLabel(dateLike?: unknown): string {
  const timezone = getBrowserTimezonePayload().customer_timezone;
  const date = coerceToDate(dateLike) ?? new Date();
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(date);
    const zoneName = parts.find((part) => part.type === 'timeZoneName')?.value;
    return zoneName ? `${timezone} (${zoneName})` : timezone;
  } catch {
    return timezone;
  }
}

export function formatInBrowserTimezone(
  value: unknown,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
): string {
  const date = coerceToDate(value);
  if (!date) return value == null || value === '' ? '-' : String(value);
  const timezone = getBrowserTimezonePayload().customer_timezone;
  try {
    return new Intl.DateTimeFormat(undefined, { ...options, timeZone: timezone }).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, options).format(date);
  }
}

export function formatTicketDateTime(raw: unknown): { date: string; time: string; compact: string; title: string } {
  if (raw == null || raw === '') return { date: '-', time: '', compact: '-', title: browserTimezoneLabel() };
  const date = formatInBrowserTimezone(raw, { day: 'numeric', month: 'short', year: 'numeric' });
  const time = formatInBrowserTimezone(raw, { hour: '2-digit', minute: '2-digit' });
  const compact = time && time !== date ? `${date}, ${time}` : date;
  return { date, time: time === date ? '' : time, compact, title: browserTimezoneLabel(raw) };
}
